import { Box } from "@mui/material";
import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { PaginationOptions } from "../../../../../components/common/BriaTable/BriaTable";
import LoadingPlaceholder from "../../../../../components/common/LoadingPlaceholder/LoadingPlaceholder.tsx";
import { useAppStore } from "../../../../../hooks/useStores";
import EvolveModelPopup from "../../../Models/Popups/EvolveModelPopup/EvolveModelPopup";
import { TgModel, TrainingMode } from "../../../Models/store/tg-model-store";
import { isLegacyTgRoute } from "../../../utils/tg-utils.ts";
import { ViewTgProjectLocationState } from "../TgProjectViewer";
import TgProjectModelsEmpty from "./Empty/TgProjectModelsEmpty";
import styles from "./TgProjectModels.module.scss";
import { ChangeVersionPopup, ExpertModelsTable, FullyAutomatedModelsTable, TrainingParametersCard } from "./components";

const TgProjectModels = () => {
	const { projectId } = useParams();
	const location = useLocation();
	const { tgStore, authStore } = useAppStore();
	const { tgProjectStore } = tgStore;
	const [loadingRow, setLoadingRow] = useState<{ data: TgModel; column: string }>();
	const [showTrainingParametersCard, setShowTrainingParametersCard] = useState<{
		model: TgModel;
		anchorEl: HTMLElement;
	} | null>(null);
	const [showChangeVersionPopup, setShowChangeVersionPopup] = useState<{
		model: TgModel;
		currentCheckpointStep: string;
		checkpointStepToBeChanged: string;
		setSelectedCheckpoint: any;
	} | null>(null);
	const [loading, setLoading] = useState(false);

	// Handle modelToEvolve from navigation state (e.g., from evaluation page)
	const locationState = location.state as ViewTgProjectLocationState | undefined;
	const [modelToEvolve, setModelToEvolve] = useState<TgModel | null>(locationState?.modelToEvolve ?? null);

	useEffect(() => {
		// Clear the navigation state after reading it to prevent popup from reopening on refresh
		if (locationState?.modelToEvolve) {
			window.history.replaceState({ ...locationState, modelToEvolve }, "");
		}
	}, []);

	const [expertModels, setExpertModels] = useState<TgModel[]>([]);
	const [fullyAutomatedModels, setFullyAutomatedModels] = useState<TgModel[]>([]);

	const [paginatedExpertModels, setPaginatedExpertModels] = useState<TgModel[]>([]);
	const [paginatedFullyAutomatedModels, setPaginatedFullyAutomatedModels] = useState<TgModel[]>([]);

	const [expertModelsMap, setExpertModelsMap] = useState<Map<number, TgModel[]>>(new Map());
	const [fullyAutomatedModelsMap, setFullyAutomatedModelsMap] = useState<Map<number, TgModel[]>>(new Map());

	const loadNextPage = (page?: number | undefined, isExpert = false) => {
		if (isExpert) {
			setPaginatedExpertModels(expertModelsMap.get(page ?? 1) ?? []);
		} else {
			setPaginatedFullyAutomatedModels(fullyAutomatedModelsMap.get(page ?? 1) ?? []);
		}
	};

	const expertPaginationOptions: PaginationOptions<TgModel> = {
		rowsPerPage: tgProjectStore.rowsPerPage,
		totalRows: expertModels.length,
		loadNextPage: async (page?: number | undefined) => loadNextPage(page, true),
	};

	const fullyAutomatedPaginationOptions: PaginationOptions<TgModel> = {
		rowsPerPage: tgProjectStore.rowsPerPage,
		totalRows: fullyAutomatedModels.length,
		loadNextPage: async (page?: number | undefined) => loadNextPage(page, false),
	};

	const paginateIntoMap = (models: TgModel[]): Map<number, TgModel[]> => {
		const pageMap = new Map<number, TgModel[]>();
		const totalPages = Math.ceil(models.length / tgProjectStore.rowsPerPage);

		for (let page = 1; page <= totalPages; page++) {
			const start = (page - 1) * tgProjectStore.rowsPerPage;
			pageMap.set(page, models.slice(start, start + tgProjectStore.rowsPerPage));
		}

		return pageMap;
	};

	const filterPaginatedModels = () => {
		const legacyRoute = isLegacyTgRoute();

		// Separate models by training mode
		const modelVersionFilter = (m: TgModel) =>
			legacyRoute ? m.training_version !== "fibo" : m.training_version === "fibo";
		const excludeDrafts = (m: TgModel) => (m as any)?.status !== "Created";

		const expertModelsList = tgProjectStore.paginatedModels.items
			.filter((model) => model.training_mode === TrainingMode.EXPERT)
			.filter(modelVersionFilter)
			.filter(excludeDrafts);
		const fullyAutomatedModelsList = tgProjectStore.paginatedModels.items
			.filter((model) => model.training_mode === TrainingMode.FULLY_AUTOMATED)
			.filter(modelVersionFilter)
			.filter(excludeDrafts);
		setExpertModels(expertModelsList);
		setFullyAutomatedModels(fullyAutomatedModelsList);

		const generatedExpertsModelsMap = paginateIntoMap(expertModelsList);
		const generatedFullyAutomatedModels = paginateIntoMap(fullyAutomatedModelsList);
		setExpertModelsMap(generatedExpertsModelsMap);
		setFullyAutomatedModelsMap(generatedFullyAutomatedModels);

		setPaginatedExpertModels(generatedExpertsModelsMap.get(1) ?? []);
		setPaginatedFullyAutomatedModels(generatedFullyAutomatedModels.get(1) ?? []);
	};

	useEffect(() => {
		const loadData = async () => {
			setLoading(true);
			await tgProjectStore.getDatasets(Number(projectId), 1, undefined, 0);

			const legacyRoute = isLegacyTgRoute();
			runInAction(() => {
				tgProjectStore.paginatedDatasets.items = tgProjectStore.paginatedDatasets.items.filter((ds) =>
					legacyRoute ? (ds?.models?.some((m) => (m as any)?.training_version !== "fibo") ?? true) : true,
				);
			});

			await tgProjectStore.getModels(Number(projectId), 1);

			filterPaginatedModels();
			setLoading(false);
		};
		if (!loading) {
			loadData();
		}
	}, []);

	useEffect(() => {
		filterPaginatedModels();
	}, [tgProjectStore.paginatedModels.items]);

	return (
		<LoadingPlaceholder
			className={styles.loadingPlaceholder}
			isLoading={
				loading ||
				tgProjectStore.loadingProjectModels ||
				tgProjectStore.loadingProjectDatasets ||
				authStore.isLoadingOrgSubscriptions
			}
		>
			<Box
				className={`${styles.container} ${
					expertModels.length === 0 && fullyAutomatedModels.length === 0 ? styles.emptyContainer : ""
				}`}
			>
				{/* Expert Models Table */}
				{expertModels.length > 0 && (
					<ExpertModelsTable
						models={paginatedExpertModels}
						loadingRow={loadingRow}
						setLoadingRow={setLoadingRow}
						setShowTrainingParametersCard={setShowTrainingParametersCard}
						setShowChangeVersionPopup={setShowChangeVersionPopup}
						showTrainingParametersCard={showTrainingParametersCard}
						paginationOptions={expertPaginationOptions}
					/>
				)}

				{/* Fully Automated Models Table */}
				{fullyAutomatedModels.length > 0 && (
					<FullyAutomatedModelsTable
						models={paginatedFullyAutomatedModels}
						loadingRow={loadingRow}
						setLoadingRow={setLoadingRow}
						paginationOptions={fullyAutomatedPaginationOptions}
					/>
				)}

				{/* Empty State */}
				{expertModels.length === 0 && fullyAutomatedModels.length === 0 && <TgProjectModelsEmpty />}

				<Box className={styles.whiteSpace} />

				{/* Training Parameters Card */}
				{showTrainingParametersCard && (
					<TrainingParametersCard
						model={showTrainingParametersCard.model}
						anchorEl={showTrainingParametersCard.anchorEl}
						onClose={() => setShowTrainingParametersCard(null)}
					/>
				)}

				{/* Change Version Popup */}
				<ChangeVersionPopup
					showChangeVersionPopup={showChangeVersionPopup}
					setShowChangeVersionPopup={setShowChangeVersionPopup}
				/>

				{/* Evolve Model Popup (from evaluation page navigation) */}
				{modelToEvolve && (
					<EvolveModelPopup
						open={!!modelToEvolve}
						onClose={() => setModelToEvolve(null)}
						model={modelToEvolve}
					/>
				)}
			</Box>
		</LoadingPlaceholder>
	);
};

export default observer(TgProjectModels);
